import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Table,
  Badge,
  IconButton,
  LinearProgress
} from '@mui/joy';
import {
  FileText,
  DownloadSimple as Download,
  ArrowsClockwise as RefreshCw,
  CheckCircle,
  Clock,
  XCircle as AlertCircle,
  MapPin,
  Eye,
  EyeSlash as EyeOff,
  Stack as Layers
} from '@phosphor-icons/react';
import api from '../../lib/api';
import { format } from 'date-fns';

interface EagleViewPanelProps {
  measurementId: number;
  onDataUpdated?: () => void;
  showOrderButton?: boolean;
}

interface EagleViewOrder {
  id: number;
  order_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  report_type: string;
  order_date: string;
  completion_date?: string;
  report_pdf_url?: string;
  expedited: boolean;
  includes_3d: boolean;
  error_message?: string;
  measurement_data?: EagleViewMeasurement;
}

interface EagleViewMeasurement {
  id: number;
  total_area_sqft: number;
  total_squares: number;
  facet_count: number;
  waste_factor: number;
  predominant_pitch: string;
  total_ridges_ft: number;
  total_valleys_ft: number;
  total_eaves_ft: number;
  total_rakes_ft: number;
  total_hips_ft?: number;
  total_perimeter_ft: number;
  measurement_date: string;
  image_date?: string;
  data_quality_score?: number;
  facets: EagleViewFacet[];
}

interface EagleViewFacet {
  id: number;
  facet_id: string;
  area_sqft: number;
  pitch: string;
  pitch_degrees: number;
  azimuth: number;
  orientation_cardinal: string;
  slope: number;
  facet_type: string;
  perimeter_ft: number;
  ridge_length_ft?: number;
  valley_length_ft?: number;
  pitch_factor: number;
}

const STATUS_COLORS = {
  pending: 'warning',
  processing: 'primary',
  completed: 'success',
  failed: 'danger'
} as const;

const STATUS_ICONS = {
  pending: <Clock size={16} />,
  processing: <RefreshCw size={16} className="animate-spin" />,
  completed: <CheckCircle size={16} />,
  failed: <AlertCircle size={16} />
};

export const EagleViewPanel: React.FC<EagleViewPanelProps> = ({
  measurementId,
  onDataUpdated,
  showOrderButton = true
}) => {
  const [orders, setOrders] = useState<EagleViewOrder[]>([]);
  const [measurementData, setMeasurementData] = useState<EagleViewMeasurement | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showAllFacets, setShowAllFacets] = useState(false);

  useEffect(() => {
    fetchEagleViewData();
  }, [measurementId]);

  const fetchEagleViewData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch orders
      const ordersResponse = await api.get(`/api/measurements/${measurementId}/eagleview_orders/`);
      const ordersData = ordersResponse.data as { results: EagleViewOrder[] };
      setOrders(ordersData.results || []);

      // Fetch measurement data
      try {
        const measurementResponse = await api.get(`/api/measurements/${measurementId}/eagleview_measurements/`);
        setMeasurementData(measurementResponse.data as EagleViewMeasurement);
      } catch (err: any) {
        if (err.response?.status !== 404) {
          console.error('Error fetching measurement data:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching EagleView data:', err);
      setError('Failed to load EagleView data');
    } finally {
      setLoading(false);
    }
  };

  const checkOrderStatus = async (order: EagleViewOrder) => {
    setRefreshing(true);
    try {
      const response = await api.get(`/api/measurements/${measurementId}/check_eagleview_status/`);
      
      // Update the order in the list
      const statusData = response.data as { status: 'pending' | 'processing' | 'completed' | 'failed' };
      setOrders(prev => prev.map(o => 
        o.id === order.id 
          ? { ...o, status: statusData.status }
          : o
      ));

      // If completed, refresh all data
      if (statusData.status === 'completed') {
        await fetchEagleViewData();
        onDataUpdated?.();
      }
    } catch (err) {
      console.error('Error checking status:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const downloadPDF = (url: string) => {
    window.open(url, '_blank');
  };

  const getLatestOrder = () => {
    return orders.length > 0 ? orders[0] : null;
  };

  const getPitchColor = (pitchDegrees: number) => {
    if (pitchDegrees < 18.4) return '#4CAF50'; // Low pitch (< 4:12)
    if (pitchDegrees < 33.7) return '#FFC107'; // Medium pitch (4:12 - 8:12)
    return '#F44336'; // Steep pitch (> 8:12)
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const latestOrder = getLatestOrder();

  return (
    <Box>
      {/* Order Status Card */}
      {latestOrder && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography level="title-md">EagleView Report Status</Typography>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <Badge
                    badgeContent={STATUS_ICONS[latestOrder.status]}
                    color={STATUS_COLORS[latestOrder.status]}
                  >
                    <Chip
                      color={STATUS_COLORS[latestOrder.status]}
                      variant="soft"
                      size="md"
                    >
                      {latestOrder.status.charAt(0).toUpperCase() + latestOrder.status.slice(1)}
                    </Chip>
                  </Badge>
                  <Typography level="body-sm">
                    Order #{latestOrder.order_id}
                  </Typography>
                  <Typography level="body-sm" sx={{ opacity: 0.7 }}>
                    {format(new Date(latestOrder.order_date), 'MMM d, yyyy h:mm a')}
                  </Typography>
                  {latestOrder.expedited && (
                    <Chip size="sm" color="warning" variant="soft">Expedited</Chip>
                  )}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1}>
                {latestOrder.status === 'completed' && latestOrder.report_pdf_url && (
                  <Button
                    size="sm"
                    variant="soft"
                    startDecorator={<Download size={16} />}
                    onClick={() => downloadPDF(latestOrder.report_pdf_url!)}
                  >
                    Download PDF
                  </Button>
                )}
                {(latestOrder.status === 'pending' || latestOrder.status === 'processing') && (
                  <Button
                    size="sm"
                    variant="soft"
                    startDecorator={refreshing ? <CircularProgress size="sm" /> : <RefreshCw size={16} />}
                    onClick={() => checkOrderStatus(latestOrder)}
                    disabled={refreshing}
                  >
                    Check Status
                  </Button>
                )}
              </Stack>
            </Stack>

            {/* Progress Bar for Processing Orders */}
            {latestOrder.status === 'processing' && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  determinate={false}
                  thickness={4}
                  sx={{ bgcolor: 'background.level2' }}
                />
                <Typography level="body-xs" sx={{ mt: 1, opacity: 0.7 }}>
                  Processing... This typically takes {latestOrder.expedited ? '30-60 minutes' : '1-4 hours'}
                </Typography>
              </Box>
            )}

            {/* Error Message */}
            {latestOrder.status === 'failed' && latestOrder.error_message && (
              <Alert color="danger" variant="soft" sx={{ mt: 2 }}>
                {latestOrder.error_message}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Measurement Data */}
      {measurementData ? (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography level="h4" startDecorator={<Layers />}>
                EagleView Measurement Data
              </Typography>
              {measurementData.data_quality_score && (
                <Chip
                  color={measurementData.data_quality_score >= 80 ? 'success' : 'warning'}
                  variant="soft"
                >
                  Quality Score: {measurementData.data_quality_score}%
                </Chip>
              )}
            </Stack>

            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
              <TabList>
                <Tab>Summary</Tab>
                <Tab>Facets ({measurementData.facet_count})</Tab>
                <Tab>Measurements</Tab>
              </TabList>

              {/* Summary Tab */}
              <TabPanel value={0}>
                <Grid container spacing={3}>
                  <Grid xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                        <Typography level="body-sm" sx={{ mb: 1 }}>Total Area</Typography>
                        <Typography level="h3">
                          {measurementData.total_area_sqft.toLocaleString()} sq ft
                        </Typography>
                        <Typography level="body-sm" sx={{ opacity: 0.7 }}>
                          {Number(measurementData.total_squares).toFixed(1)} squares
                        </Typography>
                      </Box>

                      <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                        <Typography level="body-sm" sx={{ mb: 1 }}>Predominant Pitch</Typography>
                        <Typography level="h3">
                          {measurementData.predominant_pitch}
                        </Typography>
                      </Box>

                      <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                        <Typography level="body-sm" sx={{ mb: 1 }}>Waste Factor</Typography>
                        <Typography level="h3">
                          {measurementData.waste_factor}%
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography level="body-sm">Ridges</Typography>
                          <Typography fontWeight="bold">
                            {Number(measurementData.total_ridges_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>

                      <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography level="body-sm">Valleys</Typography>
                          <Typography fontWeight="bold">
                            {Number(measurementData.total_valleys_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>

                      <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography level="body-sm">Eaves</Typography>
                          <Typography fontWeight="bold">
                            {Number(measurementData.total_eaves_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>

                      <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography level="body-sm">Rakes</Typography>
                          <Typography fontWeight="bold">
                            {Number(measurementData.total_rakes_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>

                      {measurementData.total_hips_ft && (
                        <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography level="body-sm">Hips</Typography>
                            <Typography fontWeight="bold">
                              {Number(measurementData.total_hips_ft).toFixed(1)} ft
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Grid>

                  <Grid xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" spacing={3}>
                      <Box>
                        <Typography level="body-sm">Measurement Date</Typography>
                        <Typography fontWeight="bold">
                          {format(new Date(measurementData.measurement_date), 'MMM d, yyyy')}
                        </Typography>
                      </Box>
                      {measurementData.image_date && (
                        <Box>
                          <Typography level="body-sm">Image Date</Typography>
                          <Typography fontWeight="bold">
                            {format(new Date(measurementData.image_date), 'MMM d, yyyy')}
                          </Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography level="body-sm">Total Perimeter</Typography>
                        <Typography fontWeight="bold">
                          {Number(measurementData.total_perimeter_ft).toFixed(1)} ft
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Facets Tab */}
              <TabPanel value={1}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="title-md">Individual Roof Facets</Typography>
                    <Button
                      size="sm"
                      variant="plain"
                      startDecorator={showAllFacets ? <EyeOff size={16} /> : <Eye size={16} />}
                      onClick={() => setShowAllFacets(!showAllFacets)}
                    >
                      {showAllFacets ? 'Show Summary' : 'Show All Details'}
                    </Button>
                  </Stack>

                  <Box sx={{ overflowX: 'auto' }}>
                    <Table
                      hoverRow
                      size="sm"
                      sx={{
                        '& th': { bgcolor: 'background.level2', fontWeight: 'bold' }
                      }}
                    >
                      <thead>
                        <tr>
                          <th>Facet ID</th>
                          <th>Type</th>
                          <th>Area (sq ft)</th>
                          <th>Pitch</th>
                          <th>Orientation</th>
                          <th>Pitch Factor</th>
                          {showAllFacets && (
                            <>
                              <th>Perimeter (ft)</th>
                              <th>Ridge (ft)</th>
                              <th>Valley (ft)</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {measurementData.facets.map((facet) => (
                          <tr key={facet.id}>
                            <td>{facet.facet_id}</td>
                            <td>
                              <Chip size="sm" variant="soft">
                                {facet.facet_type}
                              </Chip>
                            </td>
                            <td>{Number(facet.area_sqft).toFixed(1)}</td>
                            <td>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: getPitchColor(facet.pitch_degrees)
                                  }}
                                />
                                <Typography>{facet.pitch}</Typography>
                              </Stack>
                            </td>
                            <td>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <MapPin size={14} />
                                <Typography>{facet.orientation_cardinal}</Typography>
                                <Typography level="body-xs" sx={{ opacity: 0.7 }}>
                                  ({facet.azimuth}°)
                                </Typography>
                              </Stack>
                            </td>
                            <td>{facet.pitch_factor ? Number(facet.pitch_factor).toFixed(3) : '-'}</td>
                            {showAllFacets && (
                              <>
                                <td>{Number(facet.perimeter_ft).toFixed(1)}</td>
                                <td>{facet.ridge_length_ft ? Number(facet.ridge_length_ft).toFixed(1) : '-'}</td>
                                <td>{facet.valley_length_ft ? Number(facet.valley_length_ft).toFixed(1) : '-'}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Box>

                  {/* Facet Summary by Pitch */}
                  <Box sx={{ mt: 3 }}>
                    <Typography level="title-sm" sx={{ mb: 2 }}>Pitch Distribution</Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {Object.entries(
                        measurementData.facets.reduce((acc, facet) => {
                          const pitch = facet.pitch;
                          if (!acc[pitch]) {
                            acc[pitch] = { count: 0, area: 0 };
                          }
                          acc[pitch].count++;
                          acc[pitch].area += facet.area_sqft;
                          return acc;
                        }, {} as Record<string, { count: number; area: number }>)
                      ).map(([pitch, data]) => (
                        <Chip
                          key={pitch}
                          variant="outlined"
                          sx={{
                            borderColor: getPitchColor(
                              measurementData.facets.find(f => f.pitch === pitch)?.pitch_degrees || 0
                            )
                          }}
                        >
                          {pitch}: {data.count} facets ({Number(data.area).toFixed(0)} sq ft)
                        </Chip>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </TabPanel>

              {/* Measurements Tab */}
              <TabPanel value={2}>
                <Grid container spacing={3}>
                  <Grid xs={12} md={6}>
                    <Typography level="title-md" sx={{ mb: 2 }}>Linear Measurements</Typography>
                    <Stack spacing={1}>
                      <Box sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography>Total Ridges</Typography>
                          <Typography fontWeight="bold">
                            {Number(measurementData.total_ridges_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography>Total Valleys</Typography>
                          <Typography fontWeight="bold">
                            {Number(measurementData.total_valleys_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography>Total Eaves</Typography>
                          <Typography fontWeight="bold">
                            {Number(measurementData.total_eaves_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography>Total Rakes</Typography>
                          <Typography fontWeight="bold">
                            {Number(measurementData.total_rakes_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>
                      {measurementData.total_hips_ft && (
                        <Box sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography>Total Hips</Typography>
                            <Typography fontWeight="bold">
                              {Number(measurementData.total_hips_ft).toFixed(1)} ft
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                      <Divider />
                      <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 'sm' }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography fontWeight="bold">Total Perimeter</Typography>
                          <Typography fontWeight="bold" color="primary">
                            {Number(measurementData.total_perimeter_ft).toFixed(1)} ft
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid xs={12} md={6}>
                    <Typography level="title-md" sx={{ mb: 2 }}>Area Calculations</Typography>
                    <Stack spacing={2}>
                      <Card variant="soft" color="primary">
                        <Stack spacing={1}>
                          <Typography level="body-sm">Base Area</Typography>
                          <Typography level="h3">
                            {measurementData.total_area_sqft.toLocaleString()} sq ft
                          </Typography>
                        </Stack>
                      </Card>

                      <Card variant="soft">
                        <Stack spacing={1}>
                          <Typography level="body-sm">With Waste Factor ({measurementData.waste_factor}%)</Typography>
                          <Typography level="h3">
                            {(measurementData.total_area_sqft * (1 + measurementData.waste_factor / 100)).toLocaleString()} sq ft
                          </Typography>
                        </Stack>
                      </Card>

                      <Card variant="soft">
                        <Stack spacing={1}>
                          <Typography level="body-sm">Total Squares</Typography>
                          <Typography level="h3">
                            {Math.ceil(measurementData.total_area_sqft * (1 + measurementData.waste_factor / 100) / 100)} squares
                          </Typography>
                        </Stack>
                      </Card>
                    </Stack>
                  </Grid>
                </Grid>
              </TabPanel>
            </Tabs>
          </CardContent>
        </Card>
      ) : (
        <Alert
          startDecorator={<FileText />}
          variant="soft"
          color="neutral"
        >
          <Box>
            <Typography level="title-md">No EagleView Data Available</Typography>
            <Typography level="body-sm">
              {orders.length > 0
                ? 'Report is being processed. Check back soon.'
                : 'Order an EagleView report to get professional measurements.'}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Order History */}
      {orders.length > 1 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>Order History</Typography>
            <Stack spacing={1}>
              {orders.slice(1).map((order) => (
                <Box
                  key={order.id}
                  sx={{
                    p: 1.5,
                    bgcolor: 'background.level1',
                    borderRadius: 'sm',
                    opacity: order.status === 'failed' ? 0.7 : 1
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip
                        size="sm"
                        color={STATUS_COLORS[order.status]}
                        variant="soft"
                        startDecorator={STATUS_ICONS[order.status]}
                      >
                        {order.status}
                      </Chip>
                      <Typography level="body-sm">
                        {order.report_type} Report
                      </Typography>
                      <Typography level="body-xs" sx={{ opacity: 0.7 }}>
                        {format(new Date(order.order_date), 'MMM d, yyyy')}
                      </Typography>
                    </Stack>
                    {order.report_pdf_url && (
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => downloadPDF(order.report_pdf_url!)}
                      >
                        <Download size={16} />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};